/**
 * Charts and data visualisation. Every chart takes literal `rows` or `rowsFrom` (a binding's list), draws in the Mojo Up chart
 * tokens only (a series' `tone` picks one of the seven view tones; none picks them in order), has a legend and a tooltip, and an
 * accessible table of what it draws. HeroUI Pro's charts are recharts underneath; the kinds Pro has no component for are drawn with
 * the same library and the same tokens, so they read as one set.
 * OURS: the workbench defines this file; AIOE mirrors it.
 */
import { UI_LIMITS } from "../uiBase.js";
import { control } from "./kit.js";
export const CHART_CURVES = ["smooth", "linear", "step"];
export const CHART_STACKS = ["none", "stacked", "percent"];
const common = (k) => ({
    title: k.label.optional(),
    /** What the chart shows, in a sentence, for the table fallback and screen readers. */
    caption: k.hint.optional(),
    rows: k.rows(UI_LIMITS.chartRows),
    rowsFrom: k.rowsFrom,
    keys: k.keys,
    height: k.z.number().int().min(120).max(560).optional(),
    legend: k.z.boolean().optional(),
    unit: k.z.string().max(8).optional(),
});
const series = (k, withKind = false) => k.z
    .array(k.z
    .object({
    key: k.key,
    label: k.label.optional(),
    tone: k.tone.optional(),
    axis: k.z.enum(["left", "right"]).optional(),
    ...(withKind ? { kind: k.z.enum(["bar", "line", "area"]).optional() } : {}),
})
    .strict())
    .min(1)
    .max(UI_LIMITS.chartSeries);
const cartesian = (k) => ({ ...common(k), x: k.key, series: series(k), curve: k.z.enum(CHART_CURVES).optional(), grid: k.z.boolean().optional() });
const SALES = [
    { month: "Jul", builds: 18, failed: 2 },
    { month: "Aug", builds: 24, failed: 3 },
    { month: "Sep", builds: 31, failed: 1 },
    { month: "Oct", builds: 28, failed: 4 },
];
const SERIES = [{ key: "builds", label: "Builds", tone: "teal" }, { key: "failed", label: "Failed", tone: "rose" }];
export const CHART_CONTROLS = [
    control({
        type: "area-chart",
        group: "charts",
        heroui: "@heroui-pro/react/area-chart AreaChart",
        summary: "area-chart { title?, caption?, rows | rowsFrom, keys?, x, series[{ key, label?, tone?, axis?: left|right }], stack?: none|stacked|percent, curve?: smooth|linear|step, grid?, legend?, unit?, height? }",
        example: { type: "area-chart", title: "Builds", caption: "Builds and failures by month", rows: SALES, x: "month", series: SERIES, stack: "stacked" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...cartesian(k), stack: k.z.enum(CHART_STACKS).optional() }),
    }),
    control({
        type: "bar-chart",
        group: "charts",
        heroui: "@heroui-pro/react/bar-chart BarChart",
        summary: "bar-chart { title?, caption?, rows | rowsFrom, keys?, x, series[...], stack?: none|stacked|percent, horizontal?, grid?, legend?, unit?, height? } — grouped, stacked or 100 % stacked bars",
        example: { type: "bar-chart", title: "Builds", caption: "Builds and failures by month", rows: SALES, x: "month", series: SERIES },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...cartesian(k), stack: k.z.enum(CHART_STACKS).optional(), horizontal: k.z.boolean().optional() }),
    }),
    control({
        type: "line-chart",
        group: "charts",
        heroui: "@heroui-pro/react/line-chart LineChart",
        summary: "line-chart { title?, caption?, rows | rowsFrom, keys?, x, series[{ key, label?, tone?, axis?: left|right }], curve?, grid?, legend?, unit?, height? } — give a series axis: right for a second scale (dual axis)",
        example: { type: "line-chart", title: "Builds and failure rate", caption: "Builds on the left axis, failures on the right", rows: SALES, x: "month", series: [{ key: "builds", label: "Builds", tone: "teal" }, { key: "failed", label: "Failed", tone: "rose", axis: "right" }] },
        sources: [["rows", "rowsFrom"]],
        shape: cartesian,
    }),
    control({
        type: "composed-chart",
        group: "charts",
        heroui: "@heroui-pro/react/composed-chart ComposedChart",
        summary: "composed-chart { title?, caption?, rows | rowsFrom, keys?, x, series[{ key, label?, tone?, axis?, kind?: bar|line|area }], curve?, grid?, legend?, unit?, height? } — bars, lines and areas on one chart (dual axis too)",
        example: { type: "composed-chart", title: "Builds vs failures", caption: "Bars are builds, the line is failures", rows: SALES, x: "month", series: [{ key: "builds", label: "Builds", kind: "bar", tone: "teal" }, { key: "failed", label: "Failed", kind: "line", tone: "rose", axis: "right" }] },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), x: k.key, series: series(k, true), curve: k.z.enum(CHART_CURVES).optional(), grid: k.z.boolean().optional() }),
    }),
    control({
        type: "pie-chart",
        group: "charts",
        heroui: "@heroui-pro/react/pie-chart PieChart",
        summary: "pie-chart { title?, caption?, rows | rowsFrom, keys?, nameKey, valueKey, donut?, legend?, unit?, height? } — shares of a whole (each row one slice)",
        example: { type: "pie-chart", title: "Where the time went", caption: "Share of hours by kind of work", rows: [{ kind: "Build", hours: 12 }, { kind: "Review", hours: 7 }, { kind: "Plan", hours: 4 }], nameKey: "kind", valueKey: "hours", donut: true },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), nameKey: k.key, valueKey: k.key, donut: k.z.boolean().optional() }),
    }),
    control({
        type: "radar-chart",
        group: "charts",
        heroui: "@heroui-pro/react/radar-chart RadarChart",
        summary: "radar-chart { title?, caption?, rows | rowsFrom, keys?, x (the axis names), series[{ key, label?, tone? }], legend?, height? } — several measures of one thing on spokes",
        example: { type: "radar-chart", title: "Team health", caption: "Scores out of 10", rows: [{ axis: "Speed", now: 7, goal: 8 }, { axis: "Quality", now: 8, goal: 9 }, { axis: "Cover", now: 5, goal: 8 }, { axis: "Docs", now: 6, goal: 7 }], x: "axis", series: [{ key: "now", label: "Now", tone: "teal" }, { key: "goal", label: "Goal", tone: "amber" }] },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), x: k.key, series: series(k) }),
    }),
    control({
        type: "radial-chart",
        group: "charts",
        heroui: "@heroui-pro/react/radial-chart RadialChart",
        summary: "radial-chart { title?, caption?, rows | rowsFrom, keys?, nameKey, valueKey, max?, legend?, height? } — values as concentric rings",
        example: { type: "radial-chart", title: "Goals", caption: "Progress to each goal", rows: [{ goal: "Tests", pct: 82 }, { goal: "Docs", pct: 55 }, { goal: "Cover", pct: 38 }], nameKey: "goal", valueKey: "pct", max: 100 },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), nameKey: k.key, valueKey: k.key, max: k.z.number().positive().optional() }),
    }),
    control({
        type: "scatter-chart",
        group: "charts",
        heroui: "@heroui-pro/react/composed-chart (recharts Scatter)",
        summary: "scatter-chart { title?, caption?, rows | rowsFrom, keys?, x, y, group?, xLabel?, yLabel?, legend?, unit?, height? } — one dot per row; group colours the dots by a key",
        example: { type: "scatter-chart", title: "Size vs time", caption: "Each dot is a pull request", rows: [{ files: 3, hours: 2, team: "A" }, { files: 12, hours: 9, team: "A" }, { files: 7, hours: 4, team: "B" }, { files: 20, hours: 16, team: "B" }], x: "files", y: "hours", group: "team", xLabel: "Files", yLabel: "Hours" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), x: k.key, y: k.key, group: k.key.optional(), xLabel: k.label.optional(), yLabel: k.label.optional() }),
    }),
    control({
        type: "bubble-chart",
        group: "charts",
        heroui: "@heroui-pro/react/composed-chart (recharts Scatter, sized)",
        summary: "bubble-chart { title?, caption?, rows | rowsFrom, keys?, x, y, size, group?, xLabel?, yLabel?, legend?, height? } — a scatter whose dot area is a third measure",
        example: { type: "bubble-chart", title: "Effort, impact, cost", caption: "Bubble area is cost", rows: [{ effort: 2, impact: 8, cost: 20, area: "Web" }, { effort: 6, impact: 5, cost: 60, area: "Data" }, { effort: 9, impact: 9, cost: 90, area: "Ops" }], x: "effort", y: "impact", size: "cost", group: "area" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), x: k.key, y: k.key, size: k.key, group: k.key.optional(), xLabel: k.label.optional(), yLabel: k.label.optional() }),
    }),
    control({
        type: "histogram",
        group: "charts",
        heroui: "@heroui-pro/react/bar-chart (binned BarChart)",
        summary: "histogram { title?, caption?, rows | rowsFrom, keys?, value, bins?: 3-40, tone?, xLabel?, height? } — how the values of one key are spread, in bins",
        example: { type: "histogram", title: "Review time", caption: "Hours a pull request waited", rows: [{ h: 1 }, { h: 2 }, { h: 2 }, { h: 3 }, { h: 3 }, { h: 3 }, { h: 5 }, { h: 8 }, { h: 13 }], value: "h", bins: 6, xLabel: "Hours" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), value: k.key, bins: k.z.number().int().min(3).max(40).optional(), tone: k.tone.optional(), xLabel: k.label.optional() }),
    }),
    control({
        type: "box-plot",
        group: "charts",
        heroui: "@heroui-pro/react/composed-chart (recharts, box drawn)",
        summary: "box-plot { title?, caption?, rows | rowsFrom, keys?, group, value, tone?, height? } — the spread of one key's values per group: median, quartiles, whiskers and outliers (give raw rows; it works out the rest)",
        example: { type: "box-plot", title: "Cycle time by team", caption: "Days from start to done", rows: [{ team: "A", days: 2 }, { team: "A", days: 3 }, { team: "A", days: 4 }, { team: "A", days: 9 }, { team: "B", days: 5 }, { team: "B", days: 6 }, { team: "B", days: 8 }, { team: "B", days: 8 }], group: "team", value: "days" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), group: k.key, value: k.key, tone: k.tone.optional() }),
    }),
    control({
        type: "funnel-chart",
        group: "charts",
        heroui: "@heroui-pro/react/composed-chart (recharts Funnel)",
        summary: "funnel-chart { title?, caption?, rows | rowsFrom, keys?, nameKey, valueKey, legend?, height? } — stages that narrow, widest first",
        example: { type: "funnel-chart", title: "Sign-up", caption: "People at each step", rows: [{ step: "Visited", n: 1000 }, { step: "Signed up", n: 420 }, { step: "Activated", n: 180 }, { step: "Paid", n: 60 }], nameKey: "step", valueKey: "n" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), nameKey: k.key, valueKey: k.key }),
    }),
    control({
        type: "treemap",
        group: "charts",
        heroui: "@heroui-pro/react/composed-chart (recharts Treemap)",
        summary: "treemap { title?, caption?, rows | rowsFrom, keys?, nameKey, valueKey, groupKey?, height? } — rectangles sized by a value; groupKey nests them and colours by group",
        example: { type: "treemap", title: "Lines of code", caption: "Size of each module", rows: [{ module: "core", lines: 900, area: "Backend" }, { module: "api", lines: 400, area: "Backend" }, { module: "ui", lines: 700, area: "Frontend" }, { module: "docs", lines: 150, area: "Docs" }], nameKey: "module", valueKey: "lines", groupKey: "area" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), nameKey: k.key, valueKey: k.key, groupKey: k.key.optional() }),
    }),
    control({
        type: "heatmap",
        group: "charts",
        heroui: "@heroui-pro/react/composed-chart (cells drawn in the chart tokens)",
        summary: "heatmap { title?, caption?, kind?: matrix|calendar, rows | rowsFrom, keys?, x, y, date, value, tone?, height? } — matrix: x by y coloured by value; calendar: one cell per day from `date` (ISO), weeks across, coloured by `value`",
        example: { type: "heatmap", title: "Commits by day and hour", caption: "Darker is more", kind: "matrix", rows: [{ day: "Mon", hour: "9", n: 4 }, { day: "Mon", hour: "14", n: 9 }, { day: "Tue", hour: "9", n: 2 }, { day: "Tue", hour: "14", n: 6 }], x: "hour", y: "day", value: "n" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), kind: k.z.enum(["matrix", "calendar"]).optional(), x: k.key.optional(), y: k.key.optional(), date: k.key.optional(), value: k.key, tone: k.tone.optional() }),
    }),
    control({
        type: "sankey",
        group: "charts",
        heroui: "@heroui-pro/react/composed-chart (recharts Sankey)",
        summary: "sankey { title?, caption?, rows | rowsFrom, keys?, source, target, value, height? } — flows from one set of things to another, width by amount (each row one flow)",
        example: { type: "sankey", title: "Where tasks go", caption: "From source to outcome", rows: [{ from: "Board", to: "Done", n: 30 }, { from: "Board", to: "Dropped", n: 6 }, { from: "Issues", to: "Done", n: 12 }, { from: "Issues", to: "Board", n: 9 }], source: "from", target: "to", value: "n" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), source: k.key, target: k.key, value: k.key }),
    }),
    control({
        type: "gauge",
        group: "charts",
        heroui: "@heroui-pro/react/radial-chart (half ring)",
        summary: "gauge { label?, value, min?, max?, unit?, bands?[{ to, tone }], caption? } — one value on a dial; bands colour stretches of it up to `to`",
        example: { type: "gauge", label: "Test pass rate", value: 94, min: 0, max: 100, unit: "%", bands: [{ to: 60, tone: "rose" }, { to: 85, tone: "amber" }, { to: 100, tone: "green" }], caption: "94 percent of tests pass" },
        shape: (k) => ({
            label: k.label.optional(),
            value: k.z.union([k.num, k.z.string().max(40)]),
            min: k.num.optional(),
            max: k.num.optional(),
            unit: k.z.string().max(8).optional(),
            bands: k.z.array(k.z.object({ to: k.num, tone: k.tone }).strict()).max(5).optional(),
            caption: k.hint.optional(),
        }),
    }),
    control({
        type: "sparkline",
        group: "charts",
        heroui: "@heroui-pro/react/kpi (KPI.Chart's mini chart)",
        summary: "sparkline { points[] | rows + value | rowsFrom + value, kind?: line|area|bar, tone?, caption? } — a tiny trend line with no axes; in a table cell use column kind sparkline, in a stat use kpi points",
        example: { type: "sparkline", points: [3, 5, 4, 8, 7, 12, 10], kind: "area", tone: "teal", caption: "Rising over seven days" },
        shape: (k) => ({ points: k.numbers(120).optional(), rows: k.rows(UI_LIMITS.chartRows), rowsFrom: k.rowsFrom, keys: k.keys, value: k.key.optional(), kind: k.z.enum(["line", "area", "bar"]).optional(), tone: k.tone.optional(), caption: k.hint.optional() }),
    }),
    control({
        type: "waterfall-chart",
        group: "charts",
        heroui: "@heroui-pro/react/bar-chart (floating BarChart)",
        summary: "waterfall-chart { title?, caption?, rows | rowsFrom, keys?, x, value, totals?: string[], unit?, height? } — a running total: each row adds or subtracts; rows whose x is in totals show the total so far",
        example: { type: "waterfall-chart", title: "Budget", caption: "Start, spend, savings and the end", rows: [{ step: "Start", amount: 100 }, { step: "Hosting", amount: -30 }, { step: "Savings", amount: 12 }, { step: "End", amount: 0 }], x: "step", value: "amount", totals: ["End"], unit: "k" },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ ...common(k), x: k.key, value: k.key, totals: k.z.array(k.z.string().max(UI_LIMITS.label)).max(8).optional() }),
    }),
];
//# sourceMappingURL=charts.js.map